import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  Switch,
  FormControlLabel,
  Grid,
  Divider,
  Tabs,
  Tab,
  Avatar,
  Tooltip,
  MenuItem,
  Select,
  InputLabel,
  FormControl
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StorefrontIcon from '@mui/icons-material/Storefront';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import { collection, getDocs, doc, setDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import type { BusinessAd } from '../../components/BusinessPromotions';

const GOLD = '#d4af37';

function toSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

// Default mock data to seed Firestore
const defaultBusinesses: Omit<BusinessAd, 'id'>[] = [
  {
    businessName: 'Krishna Silks & Textiles',
    tagline: 'Traditional Kerala Handlooms & Festive Collections',
    category: 'Shopping',
    bannerUrl: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=800&q=80',
    description: 'Premium handloom sarees, set mundus, and festive wear for all occasions. Visit our showroom near Muchukunnu junction for exclusive temple festival collections.',
    phone: '+91 98765 43210',
    whatsapp: '+919876543210',
    email: 'krishnasilks@example.com',
    website: 'https://example.com',
    address: 'Near Muchukunnu Junction, Kozhikode, Kerala 673012',
    mapUrl: 'https://www.google.com/maps/dir/?api=1&destination=Muchukunnu+Junction,+Kozhikode,+Kerala',
    offer: '20% Off Festival Collection',
    openingHours: '9:00 AM – 9:00 PM (Mon–Sat), 10:00 AM – 6:00 PM (Sun)',
    established: '1995',
    galleryImages: [
      'https://images.unsplash.com/photo-1562157873-818bc0726f68?w=600&q=80',
      'https://images.unsplash.com/photo-1594040226829-7f251ab46d80?w=600&q=80',
      'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=600&q=80',
    ],
    youtubeVideos: [],
    socialLinks: { facebook: '', instagram: '', youtube: '', twitter: '' },
    products: [
      { name: 'Kasavu Saree Collection', image: 'https://images.unsplash.com/photo-1562157873-818bc0726f68?w=400&q=80', price: '₹2,500 – ₹15,000', description: 'Traditional Kerala Kasavu sarees with gold zari border' },
    ],
    highlights: [
      { icon: 'verified', title: 'Authentic Handlooms', description: 'Directly sourced from Balaramapuram weavers' },
    ],
    isActive: true,
  },
  {
    businessName: 'Annapoorna Pure Veg Restaurant',
    tagline: 'Authentic Kerala Sadya & South Indian Cuisine',
    category: 'Restaurant',
    bannerUrl: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=80',
    description: 'Experience the taste of traditional Kerala cuisine. Special sadya available on festival days.',
    phone: '+91 98765 43211',
    whatsapp: '+919876543211',
    email: 'annapoorna@example.com',
    address: '200m from Kottayil Temple, Muchukunnu, Kozhikode, Kerala',
    mapUrl: 'https://www.google.com/maps/dir/?api=1&destination=Muchukunnu,+Kozhikode,+Kerala',
    offer: 'Free Payasam with Sadya',
    openingHours: '7:00 AM – 10:00 PM (All Days)',
    established: '2008',
    galleryImages: [],
    youtubeVideos: [],
    socialLinks: { facebook: '', instagram: '', youtube: '', twitter: '' },
    products: [],
    highlights: [],
    isActive: true,
  },
  {
    businessName: 'Luxing Tours and Travels',
    tagline: 'Tempo Traveller Hire, Airport Transfers & Kerala Tours',
    category: 'Travel',
    bannerUrl: 'https://www.chennaitravels.in/wp-content/uploads/2025/01/18-seater-tempo.jpg',
    description: 'Premium tempo traveller hire for temple trips, airport transfers, wedding convoys, and full Kerala tour packages. AC Tempo Travellers available 24/7.',
    phone: '+91 98765 43220',
    whatsapp: '+919876543220',
    address: 'Mavoor Road, Kozhikode, Kerala',
    mapUrl: 'https://www.google.com/maps/dir/?api=1&destination=Mavoor+Road,+Kozhikode,+Kerala',
    openingHours: '24/7 — Round the Clock Service',
    established: '2015',
    offer: 'Flat 10% Off on Temple Tour Packages',
    galleryImages: [
      'https://www.chennaitravels.in/wp-content/uploads/2025/01/18-seater-tempo.jpg',
      'https://www.chennaitravels.in/wp-content/uploads/2025/01/luxury-tempo-in-chennai.jpg',
    ],
    youtubeVideos: [],
    socialLinks: { facebook: '', instagram: '', youtube: '', twitter: '' },
    products: [
      { name: '12 Seater Tempo Traveller', image: 'https://www.chennaitravels.in/wp-content/uploads/2025/01/18-seater-tempo.jpg', price: '₹22/km', description: 'Comfortable AC tempo for small group trips' },
    ],
    highlights: [
      { icon: 'schedule', title: '24/7 Available', description: 'Round the clock booking & dispatch' },
    ],
    isActive: true,
  },
  {
    businessName: 'Calicut Gold & Jewellers',
    tagline: 'BIS Hallmarked Gold & Temple Jewellery',
    category: 'Jewellery',
    bannerUrl: 'https://images.unsplash.com/photo-1573408301185-9146fe634ad0?w=800&q=80',
    description: 'Exclusive collection of traditional Kerala temple jewellery. BIS hallmarked gold ornaments, silver pooja items, and custom designs.',
    phone: '+91 98765 43213',
    whatsapp: '+919876543213',
    email: 'calicutgold@example.com',
    website: 'https://example.com',
    address: 'SM Street, Kozhikode, Kerala',
    mapUrl: 'https://www.google.com/maps/dir/?api=1&destination=SM+Street,+Kozhikode,+Kerala',
    offer: 'Making Charge Waiver on Festival Days',
    openingHours: '10:00 AM – 8:30 PM (Mon–Sat)',
    established: '1982',
    galleryImages: [],
    youtubeVideos: [],
    socialLinks: { facebook: '', instagram: '', youtube: '', twitter: '' },
    products: [],
    highlights: [],
    isActive: true,
  },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function BusinessPromotionsManager() {
  const [businesses, setBusinesses] = useState<BusinessAd[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [sectionVisible, setSectionVisible] = useState(true);
  const [sectionVisibleLoading, setSectionVisibleLoading] = useState(true);

  // Basic info form fields
  const [businessName, setBusinessName] = useState('');
  const [tagline, setTagline] = useState('');
  const [category, setCategory] = useState('');
  const [bannerUrl, setBannerUrl] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Contact fields
  const [phone, setPhone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [address, setAddress] = useState('');
  const [mapUrl, setMapUrl] = useState('');

  // Extra fields
  const [offer, setOffer] = useState('');
  const [openingHours, setOpeningHours] = useState('');
  const [established, setEstablished] = useState('');

  // Gallery
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [newGalleryUrl, setNewGalleryUrl] = useState('');

  // YouTube
  const [youtubeVideos, setYoutubeVideos] = useState<string[]>([]);
  const [newYoutubeUrl, setNewYoutubeUrl] = useState('');

  // Social links
  const [facebook, setFacebook] = useState('');
  const [instagram, setInstagram] = useState('');
  const [youtube, setYoutube] = useState('');
  const [twitter, setTwitter] = useState('');

  // Products
  const [products, setProducts] = useState<{ name: string; image: string; price?: string; description?: string }[]>([]);

  // Highlights
  const [highlights, setHighlights] = useState<{ icon: string; title: string; description: string }[]>([]);

  // Subscription
  const [subscription, setSubscription] = useState<'free' | '6months' | '1year'>('free');
  const [subscriptionStartDate, setSubscriptionStartDate] = useState('');
  const [subscriptionEndDate, setSubscriptionEndDate] = useState('');

  useEffect(() => {
    loadBusinesses();
    loadSectionVisibility();
  }, []);

  const loadSectionVisibility = async () => {
    try {
      const snap = await getDoc(doc(db, 'homepage_content', 'business_promotions_section'));
      if (snap.exists()) {
        setSectionVisible(snap.data().visible !== false);
      }
    } catch (err) {
      console.error('Error loading section visibility:', err);
    } finally {
      setSectionVisibleLoading(false);
    }
  };

  const handleToggleSectionVisibility = async () => {
    const newValue = !sectionVisible;
    try {
      await setDoc(doc(db, 'homepage_content', 'business_promotions_section'), { visible: newValue, updatedAt: new Date().toISOString() }, { merge: true });
      setSectionVisible(newValue);
      setSnackbar({ open: true, message: newValue ? 'Section is now visible on website' : 'Section is now hidden from website', severity: 'success' });
    } catch (err) {
      console.error('Error toggling section visibility:', err);
      setSnackbar({ open: true, message: 'Error updating section visibility', severity: 'error' });
    }
  };

  const loadBusinesses = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'business_promotions'));
      const data: BusinessAd[] = [];
      querySnapshot.forEach((docSnap) => {
        data.push({ id: docSnap.id, ...docSnap.data() } as BusinessAd);
      });
      data.sort((a, b) => a.businessName.localeCompare(b.businessName));
      setBusinesses(data);
    } catch (error) {
      console.error('Error loading businesses:', error);
      setSnackbar({ open: true, message: 'Error loading businesses', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('This will add default business entries to Firestore. Existing entries with the same slug will be overwritten. Continue?')) return;
    try {
      for (const biz of defaultBusinesses) {
        const slug = toSlug(biz.businessName);
        const docRef = doc(db, 'business_promotions', slug);
        await setDoc(docRef, { ...biz, slug, createdAt: new Date().toISOString() });
      }
      setSnackbar({ open: true, message: 'Default businesses seeded successfully!', severity: 'success' });
      loadBusinesses();
    } catch (error) {
      console.error('Error seeding defaults:', error);
      setSnackbar({ open: true, message: 'Error seeding defaults', severity: 'error' });
    }
  };

  const handleSave = async () => {
    if (!businessName.trim() || !category.trim()) {
      setSnackbar({ open: true, message: 'Business name and category are required', severity: 'error' });
      return;
    }

    try {
      const slug = toSlug(businessName);
      const docId = editingId || slug;
      const docRef = doc(db, 'business_promotions', docId);

      const businessData: Omit<BusinessAd, 'id'> = {
        businessName: businessName.trim(),
        tagline: tagline.trim(),
        category: category.trim(),
        bannerUrl: bannerUrl.trim(),
        description: description.trim(),
        phone: phone.trim(),
        whatsapp: whatsapp.trim(),
        email: email.trim(),
        website: website.trim(),
        address: address.trim(),
        mapUrl: mapUrl.trim(),
        offer: offer.trim(),
        openingHours: openingHours.trim(),
        established: established.trim(),
        galleryImages,
        youtubeVideos,
        socialLinks: {
          facebook: facebook.trim(),
          instagram: instagram.trim(),
          youtube: youtube.trim(),
          twitter: twitter.trim(),
        },
        products,
        highlights,
        isActive,
        subscription,
        ...(subscriptionStartDate ? { subscriptionStartDate } : {}),
        ...(subscriptionEndDate ? { subscriptionEndDate } : {}),
      };

      const saveData: Record<string, any> = { ...businessData, slug, updatedAt: new Date().toISOString() };
      if (!editingId) saveData.createdAt = new Date().toISOString();
      if (!subscriptionStartDate) { saveData.subscriptionStartDate = ''; }
      if (!subscriptionEndDate) { saveData.subscriptionEndDate = ''; }
      await setDoc(docRef, saveData, { merge: true });

      setSnackbar({
        open: true,
        message: editingId ? 'Business updated successfully!' : 'Business added successfully!',
        severity: 'success',
      });

      resetForm();
      setDialogOpen(false);
      loadBusinesses();
    } catch (error) {
      console.error('Error saving business:', error);
      setSnackbar({ open: true, message: 'Error saving business', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this business? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'business_promotions', id));
      setSnackbar({ open: true, message: 'Business deleted successfully!', severity: 'success' });
      loadBusinesses();
    } catch (error) {
      console.error('Error deleting business:', error);
      setSnackbar({ open: true, message: 'Error deleting business', severity: 'error' });
    }
  };

  const handleEdit = (biz: BusinessAd) => {
    setEditingId(biz.id);
    setBusinessName(biz.businessName);
    setTagline(biz.tagline || '');
    setCategory(biz.category);
    setBannerUrl(biz.bannerUrl || '');
    setDescription(biz.description || '');
    setIsActive(biz.isActive);
    setPhone(biz.phone || '');
    setWhatsapp(biz.whatsapp || '');
    setEmail(biz.email || '');
    setWebsite(biz.website || '');
    setAddress(biz.address || '');
    setMapUrl(biz.mapUrl || '');
    setOffer(biz.offer || '');
    setOpeningHours(biz.openingHours || '');
    setEstablished(biz.established || '');
    setGalleryImages(biz.galleryImages || []);
    setYoutubeVideos(biz.youtubeVideos || []);
    setFacebook(biz.socialLinks?.facebook || '');
    setInstagram(biz.socialLinks?.instagram || '');
    setYoutube(biz.socialLinks?.youtube || '');
    setTwitter(biz.socialLinks?.twitter || '');
    setProducts(biz.products || []);
    setHighlights(biz.highlights || []);
    setSubscription(biz.subscription || 'free');
    setSubscriptionStartDate(biz.subscriptionStartDate || '');
    setSubscriptionEndDate(biz.subscriptionEndDate || '');
    setTabValue(0);
    setDialogOpen(true);
  };

  const handleToggleActive = async (biz: BusinessAd) => {
    try {
      const docRef = doc(db, 'business_promotions', biz.id);
      await setDoc(docRef, { isActive: !biz.isActive, updatedAt: new Date().toISOString() }, { merge: true });
      setSnackbar({ open: true, message: `Business ${!biz.isActive ? 'activated' : 'deactivated'}!`, severity: 'success' });
      loadBusinesses();
    } catch (error) {
      console.error('Error toggling status:', error);
      setSnackbar({ open: true, message: 'Error updating status', severity: 'error' });
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setBusinessName('');
    setTagline('');
    setCategory('');
    setBannerUrl('');
    setDescription('');
    setIsActive(true);
    setPhone('');
    setWhatsapp('');
    setEmail('');
    setWebsite('');
    setAddress('');
    setMapUrl('');
    setOffer('');
    setOpeningHours('');
    setEstablished('');
    setGalleryImages([]);
    setNewGalleryUrl('');
    setYoutubeVideos([]);
    setNewYoutubeUrl('');
    setFacebook('');
    setInstagram('');
    setYoutube('');
    setTwitter('');
    setProducts([]);
    setHighlights([]);
    setSubscription('free');
    setSubscriptionStartDate('');
    setSubscriptionEndDate('');
    setTabValue(0);
  };

  const addGalleryImage = () => {
    if (newGalleryUrl.trim()) {
      setGalleryImages([...galleryImages, newGalleryUrl.trim()]);
      setNewGalleryUrl('');
    }
  };

  const removeGalleryImage = (index: number) => {
    setGalleryImages(galleryImages.filter((_, i) => i !== index));
  };

  const addYoutubeVideo = () => {
    if (newYoutubeUrl.trim()) {
      setYoutubeVideos([...youtubeVideos, newYoutubeUrl.trim()]);
      setNewYoutubeUrl('');
    }
  };

  const removeYoutubeVideo = (index: number) => {
    setYoutubeVideos(youtubeVideos.filter((_, i) => i !== index));
  };

  const addProduct = () => {
    setProducts([...products, { name: '', image: '', price: '', description: '' }]);
  };

  const updateProduct = (index: number, field: string, value: string) => {
    const updated = [...products];
    (updated[index] as any)[field] = value;
    setProducts(updated);
  };

  const removeProduct = (index: number) => {
    setProducts(products.filter((_, i) => i !== index));
  };

  const addHighlight = () => {
    setHighlights([...highlights, { icon: '', title: '', description: '' }]);
  };

  const updateHighlight = (index: number, field: string, value: string) => {
    const updated = [...highlights];
    (updated[index] as any)[field] = value;
    setHighlights(updated);
  };

  const removeHighlight = (index: number) => {
    setHighlights(highlights.filter((_, i) => i !== index));
  };

  const categoryColors: Record<string, string> = {
    Shopping: '#e91e63',
    Restaurant: '#ff9800',
    'Pooja Items': '#4caf50',
    Jewellery: '#9c27b0',
    Hotel: '#2196f3',
    Travel: '#00bcd4',
    Services: '#607d8b',
  };

  return (
    <Box>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 4, mb: 3, background: `linear-gradient(135deg, ${GOLD}15 0%, #fff 100%)` }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <StorefrontIcon sx={{ color: GOLD }} />
              Business Promotions Manager
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage featured businesses, promotions, and local service listings
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={sectionVisible}
                  onChange={handleToggleSectionVisibility}
                  disabled={sectionVisibleLoading}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: GOLD }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: GOLD } }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600, color: sectionVisible ? '#4caf50' : '#f44336' }}>
                  {sectionVisible ? 'Section visible on website' : 'Section hidden from website'}
                </Typography>
              }
              sx={{ mt: 1 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {businesses.length === 0 && (
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleSeedDefaults}
                sx={{ borderColor: GOLD, color: GOLD, fontWeight: 700, '&:hover': { bgcolor: `${GOLD}10`, borderColor: GOLD } }}
              >
                Seed Defaults
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => { resetForm(); setDialogOpen(true); }}
              sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700, '&:hover': { bgcolor: '#c49b2e' } }}
            >
              Add Business
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: GOLD }} />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: `${GOLD}20` }}>
                <TableCell sx={{ fontWeight: 700 }}>Business</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Offer</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Subscription</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Views</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Active</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {businesses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">
                      No businesses found. Click "Seed Defaults" to add sample data or "Add Business" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                businesses.map((biz) => (
                  <TableRow key={biz.id} hover sx={{ opacity: biz.isActive ? 1 : 0.5 }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          src={biz.bannerUrl}
                          variant="rounded"
                          sx={{ width: 48, height: 48 }}
                        >
                          <StorefrontIcon />
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{biz.businessName}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {biz.tagline}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={biz.category}
                        size="small"
                        sx={{ bgcolor: categoryColors[biz.category] || '#9e9e9e', color: '#fff', fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell>{biz.phone || '—'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 180 }} noWrap>
                        {biz.offer || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          biz.subscription === '1year' ? '1 Year' :
                          biz.subscription === '6months' ? '6 Months' : 'Free'
                        }
                        size="small"
                        sx={{
                          bgcolor: biz.subscription === '1year' ? '#4caf50' : biz.subscription === '6months' ? '#2196f3' : '#9e9e9e',
                          color: '#fff',
                          fontWeight: 700,
                        }}
                      />
                      {biz.subscription !== 'free' && biz.subscriptionEndDate && (
                        <Typography variant="caption" display="block" color={new Date(biz.subscriptionEndDate) < new Date() ? 'error' : 'text.secondary'} sx={{ mt: 0.5 }}>
                          {new Date(biz.subscriptionEndDate) < new Date() ? 'Expired' : `Ends: ${new Date(biz.subscriptionEndDate).toLocaleDateString()}`}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {biz.viewCount || 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={biz.isActive ? 'Click to deactivate' : 'Click to activate'}>
                        <IconButton size="small" onClick={() => handleToggleActive(biz)}>
                          {biz.isActive ? (
                            <VisibilityIcon sx={{ color: '#4caf50' }} />
                          ) : (
                            <VisibilityOffIcon sx={{ color: '#9e9e9e' }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleEdit(biz)} sx={{ color: GOLD, mr: 1 }}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(biz.id)} sx={{ color: '#f44336' }}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: `${GOLD}20`, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <StorefrontIcon sx={{ color: GOLD }} />
          {editingId ? 'Edit Business' : 'Add New Business'}
        </DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, '& .MuiTab-root': { fontWeight: 600 }, '& .Mui-selected': { color: GOLD } }}
            TabIndicatorProps={{ style: { backgroundColor: GOLD } }}
          >
            <Tab label="Basic Info" />
            <Tab label="Contact" />
            <Tab label="Gallery & Media" />
            <Tab label="Products" />
            <Tab label="Highlights" />
            <Tab label="Subscription" />
          </Tabs>

          {/* Tab 0: Basic Info */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <TextField fullWidth label="Business Name *" value={businessName} onChange={(e) => setBusinessName(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Category *" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Shopping, Restaurant, Travel..." />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Tagline" value={tagline} onChange={(e) => setTagline(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Banner Image URL" value={bannerUrl} onChange={(e) => setBannerUrl(e.target.value)} />
                {bannerUrl && (
                  <Box sx={{ mt: 1, borderRadius: 2, overflow: 'hidden', maxHeight: 200 }}>
                    <img src={bannerUrl} alt="Banner preview" style={{ width: '100%', objectFit: 'cover', maxHeight: 200 }} />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={4} label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Offer / Discount" value={offer} onChange={(e) => setOffer(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Opening Hours" value={openingHours} onChange={(e) => setOpeningHours(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Established Year" value={established} onChange={(e) => setEstablished(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} sx={{ '& .Mui-checked': { color: GOLD }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: GOLD } }} />}
                  label="Active (visible to public)"
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 1: Contact */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="WhatsApp" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Website" value={website} onChange={(e) => setWebsite(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Address" value={address} onChange={(e) => setAddress(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Google Maps Direction URL" value={mapUrl} onChange={(e) => setMapUrl(e.target.value)} />
              </Grid>
              <Divider sx={{ width: '100%', my: 2 }} />
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Social Media Links</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Facebook URL" value={facebook} onChange={(e) => setFacebook(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Instagram URL" value={instagram} onChange={(e) => setInstagram(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="YouTube URL" value={youtube} onChange={(e) => setYoutube(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Twitter / X URL" value={twitter} onChange={(e) => setTwitter(e.target.value)} />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 2: Gallery & Media */}
          <TabPanel value={tabValue} index={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Gallery Images</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Image URL"
                value={newGalleryUrl}
                onChange={(e) => setNewGalleryUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGalleryImage(); } }}
              />
              <Button variant="outlined" startIcon={<AddPhotoAlternateIcon />} onClick={addGalleryImage} sx={{ borderColor: GOLD, color: GOLD, whiteSpace: 'nowrap' }}>
                Add
              </Button>
            </Box>
            <Grid container spacing={1}>
              {galleryImages.map((img, idx) => (
                <Grid item xs={4} sm={3} key={idx}>
                  <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
                    <img src={img} alt={`Gallery ${idx + 1}`} style={{ width: '100%', height: 80, objectFit: 'cover' }} />
                    <IconButton
                      size="small"
                      onClick={() => removeGalleryImage(idx)}
                      sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', '&:hover': { bgcolor: '#f44336' }, width: 24, height: 24 }}
                    >
                      <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>YouTube Videos</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="YouTube URL"
                value={newYoutubeUrl}
                onChange={(e) => setNewYoutubeUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addYoutubeVideo(); } }}
              />
              <Button variant="outlined" onClick={addYoutubeVideo} sx={{ borderColor: GOLD, color: GOLD, whiteSpace: 'nowrap' }}>
                Add
              </Button>
            </Box>
            {youtubeVideos.map((url, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {url}
                </Typography>
                <IconButton size="small" onClick={() => removeYoutubeVideo(idx)} sx={{ color: '#f44336' }}>
                  <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            ))}
          </TabPanel>

          {/* Tab 3: Products */}
          <TabPanel value={tabValue} index={3}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Products / Services</Typography>
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addProduct} sx={{ borderColor: GOLD, color: GOLD }}>
                Add Product
              </Button>
            </Box>
            {products.map((prod, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: GOLD }}>Product #{idx + 1}</Typography>
                  <IconButton size="small" onClick={() => removeProduct(idx)} sx={{ color: '#f44336' }}>
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Name" value={prod.name} onChange={(e) => updateProduct(idx, 'name', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Price" value={prod.price || ''} onChange={(e) => updateProduct(idx, 'price', e.target.value)} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="Image URL" value={prod.image} onChange={(e) => updateProduct(idx, 'image', e.target.value)} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="Description" value={prod.description || ''} onChange={(e) => updateProduct(idx, 'description', e.target.value)} />
                  </Grid>
                </Grid>
              </Paper>
            ))}
            {products.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No products added. Click "Add Product" to create one.
              </Typography>
            )}
          </TabPanel>

          {/* Tab 4: Highlights */}
          <TabPanel value={tabValue} index={4}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Business Highlights</Typography>
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addHighlight} sx={{ borderColor: GOLD, color: GOLD }}>
                Add Highlight
              </Button>
            </Box>
            {highlights.map((hl, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: GOLD }}>Highlight #{idx + 1}</Typography>
                  <IconButton size="small" onClick={() => removeHighlight(idx)} sx={{ color: '#f44336' }}>
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Icon Name" value={hl.icon} onChange={(e) => updateHighlight(idx, 'icon', e.target.value)} placeholder="e.g. verified, schedule" />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField fullWidth size="small" label="Title" value={hl.title} onChange={(e) => updateHighlight(idx, 'title', e.target.value)} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="Description" value={hl.description} onChange={(e) => updateHighlight(idx, 'description', e.target.value)} />
                  </Grid>
                </Grid>
              </Paper>
            ))}
            {highlights.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No highlights added. Click "Add Highlight" to create one.
              </Typography>
            )}
          </TabPanel>

          {/* Tab 5: Subscription */}
          <TabPanel value={tabValue} index={5}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <SubscriptionsIcon sx={{ color: GOLD }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Subscription Plan</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Subscription Tier</InputLabel>
                  <Select
                    value={subscription}
                    label="Subscription Tier"
                    onChange={(e) => {
                      const tier = e.target.value as 'free' | '6months' | '1year';
                      setSubscription(tier);
                      if (tier === 'free') {
                        setSubscriptionStartDate('');
                        setSubscriptionEndDate('');
                      } else if (tier === '6months' || tier === '1year') {
                        const start = new Date().toISOString().split('T')[0];
                        setSubscriptionStartDate(start);
                        const end = new Date();
                        if (tier === '6months') end.setMonth(end.getMonth() + 6);
                        else end.setFullYear(end.getFullYear() + 1);
                        setSubscriptionEndDate(end.toISOString().split('T')[0]);
                      }
                    }}
                  >
                    <MenuItem value="free">Free</MenuItem>
                    <MenuItem value="6months">6 Months</MenuItem>
                    <MenuItem value="1year">1 Year</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: subscription === '1year' ? '#e8f5e9' : subscription === '6months' ? '#e3f2fd' : '#f5f5f5' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: subscription === '1year' ? '#4caf50' : subscription === '6months' ? '#2196f3' : '#9e9e9e' }}>
                    {subscription === '1year' ? '1 Year Plan' : subscription === '6months' ? '6 Months Plan' : 'Free Plan'}
                  </Typography>
                </Paper>
              </Grid>
              {subscription !== 'free' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      type="date"
                      value={subscriptionStartDate}
                      onChange={(e) => setSubscriptionStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="End Date"
                      type="date"
                      value={subscriptionEndDate}
                      onChange={(e) => setSubscriptionEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  {subscriptionEndDate && new Date(subscriptionEndDate) < new Date() && (
                    <Grid item xs={12}>
                      <Alert severity="warning">
                        This subscription has expired. Consider renewing or switching to Free.
                      </Alert>
                    </Grid>
                  )}
                </>
              )}
              {editingId && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Total Views:</Typography>
                    <Chip
                      label={businesses.find(b => b.id === editingId)?.viewCount || 0}
                      sx={{ bgcolor: `${GOLD}20`, fontWeight: 700 }}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </TabPanel>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#666' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700, '&:hover': { bgcolor: '#c49b2e' } }}
          >
            {editingId ? 'Update Business' : 'Add Business'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
